import type { Route, RoutePreloadDataContext } from "types/not-yet-sorted"
import { lazyWithPreload } from "utils/lazyWithPreload"
import { Navigate, useLocation } from "react-router-dom"
import { universalRoutes } from "./universal"

const Labelling = lazyWithPreload(() => import("pages/roads/Labelling"))
// super-admin only: kept out of the entry chunk (it pulls in PrimeReact DataTable & co.)
const IntegrationStatus = lazyWithPreload(() => import("pages/common/Integrations"))
const Detections = lazyWithPreload(() => import("pages/roads/Detections"))
const DetectionsV2 = lazyWithPreload(() => import("pages/roads/DetectionsV2"))
const RouteView = lazyWithPreload(() => import("pages/roads/RouteView"))
const RouteViewV2 = lazyWithPreload(() => import("pages/roads/RouteViewV2"))
const NetworkView = lazyWithPreload(() => import("pages/roads/NetworkView"))
const Exports = lazyWithPreload(() => import("pages/roads/Exports"))
const Repairs = lazyWithPreload(() => import("pages/roads/Repairs"))
const Signs = lazyWithPreload(() => import("pages/roads/Signs"))
const Dashboard = lazyWithPreload(() => import("pages/roads/Dashboard"))
const RoadConditionOverview = lazyWithPreload(() => import("pages/roads/RoadCondition/RoadConditionOverview"))
const RoadConditionDefectsGuide = lazyWithPreload(() => import("pages/roads/RoadCondition/DefectGuide"))
const RoadConditionDetail = lazyWithPreload(() => import("pages/roads/RoadCondition/RoadConditionDetail"))
const Devices = lazyWithPreload(() => import("pages/roads/Devices"))
const DeviceManagement = lazyWithPreload(() => import("pages/roads/DeviceManagement"))
const Customers = lazyWithPreload(() => import("pages/roads/Customers"))
const Analytics = lazyWithPreload(() => import("pages/roads/Analytics"))
const ConfirmExports = lazyWithPreload(() => import("pages/roads/ConfirmExports"))
const WorkItemDetail = lazyWithPreload(() => import("pages/roads/WorkItems/WorkItemDetail"))
const Notifications = lazyWithPreload(() => import("pages/roads/Notifications"))
const WorkItems = lazyWithPreload(() => import("pages/roads/WorkItems"))
const BookmarkImage = lazyWithPreload(() => import("pages/roads/BookmarkImage"))
const DetectionImage = lazyWithPreload(() => import("pages/roads/DetectionImage"))

// Old combined Inbox is split into Notifications + Assigned Work.
// Keep /inbox working for stale links/bookmarks.
const InboxRedirect = () => <Navigate to="/notifications" replace />

// "Customer Behaviour" was renamed to Analytics (URL and naming).
// Keep /customer-behaviour working for stale links/bookmarks — including the
// ?month=&customer= state the page writes to the URL, so a shared link still
// opens the same month and customer.
const CustomerBehaviourRedirect = () => {
    const { search } = useLocation()
    return <Navigate to={`/analytics${search}`} replace />
}

export const roadsRoutes: Route[] = [
    {
        path: "/detections",
        component: Detections,
        meta: { breadcrumb: [{ parent: "Dashboard", label: "Detections (Legacy)" }] },
        internal: false,
    },
    {
        path: "/confirm-exports",
        component: ConfirmExports,
        meta: { breadcrumb: [{ parent: "Dashboard", label: "Confirm Exports" }] },
        internal: false,
    },
    {
        path: "/detections-v2",
        component: DetectionsV2,
        // An item link (?cluster_id=…): the item, its thread / work items /
        // export state and frame start while the page's chunk downloads. The
        // prefetch module is its own small chunk, loaded only for item links.
        preloadData: ({ queryClient, customerId, search }: RoutePreloadDataContext) => {
            if (!new URLSearchParams(search).has("cluster_id")) return
            import("pages/roads/DetectionsV2/queries/prefetchDetectionDetail")
                .then(({ prefetchDetectionsV2FromUrl }) => prefetchDetectionsV2FromUrl(queryClient, customerId, search))
                .catch(() => undefined)
        },
        meta: {
            breadcrumb: [{ parent: "Dashboard", label: "Detections V2" }],
            helpUrl: "https://help.routereports.com/en/articles/7025459-detections-page",
        },
        internal: false,
    },
    {
        path: "/route-view/*",
        component: RouteView,
        meta: { breadcrumb: [{ parent: "Dashboard", label: "Route View" }] },
        internal: false,
    },
    {
        path: "/route-view-v2",
        component: RouteViewV2,
        meta: { breadcrumb: [{ parent: "Dashboard", label: "Route View V2" }] },
        internal: false,
    },
    {
        path: "/network-view",
        component: NetworkView,
        meta: { breadcrumb: [{ parent: "Dashboard", label: "Road Condition V2" }] },
        // Customer-visible since Sep 2026 (super-admin only while it was a prototype).
        internal: false,
    },
    {
        path: "/exports",
        component: Exports,
        meta: { breadcrumb: [{ parent: "Dashboard", label: "Exports" }] },
        // ART-3002: customer-visible — Detections V2 background exports land here.
        internal: false,
    },
    {
        path: "/road-condition",
        component: RoadConditionOverview,
        meta: { breadcrumb: [{ parent: "Dashboard", label: "Road Condition (Legacy)" }] },
        internal: false,
    },
    {
        path: "/road-condition-defects-guide",
        component: RoadConditionDefectsGuide,
        meta: { breadcrumb: [{ parent: "Dashboard", label: "Defects Guide" }] },
        internal: false,
    },
    {
        path: "/road-condition/detail",
        component: RoadConditionDetail,
        meta: {
            breadcrumb: [
                {
                    parent: "Dashboard",
                    label: "Road Condition Detail",
                    regex: "/road-condition/detail/(.*?)",
                },
            ],
        },
    },
    {
        path: "/repairs",
        component: Repairs,
        meta: { breadcrumb: [{ parent: "Dashboard", label: "Repairs" }] },
        internal: false,
    },
    {
        path: "/signs",
        component: Signs,
        meta: { breadcrumb: [{ parent: "Dashboard", label: "Signs" }] },
        internal: false,
    },
    {
        path: "/dashboard",
        component: Dashboard,
        meta: { breadcrumb: [{ parent: "Dashboard", label: "Dashboard" }] },
        internal: false,
    },
    {
        path: "/devices",
        component: Devices,
        meta: { breadcrumb: [{ parent: "Dashboard", label: "Devices" }] },
        internal: false,
    },
    {
        path: "/device-management",
        component: DeviceManagement,
        meta: { breadcrumb: [{ parent: "Dashboard", label: "Device Management" }] },
        internal: true,
    },
    {
        path: "/customers",
        component: Customers,
        meta: { breadcrumb: [{ parent: "Dashboard", label: "Customer Configuration" }] },
        internal: true,
    },
    {
        path: "/analytics",
        component: Analytics,
        meta: { breadcrumb: [{ parent: "Dashboard", label: "Analytics" }] },
        internal: true,
    },
    {
        path: "/customer-behaviour",
        component: CustomerBehaviourRedirect,
        meta: { breadcrumb: [{ parent: "Dashboard", label: "Analytics" }] },
        internal: true,
    },
    {
        path: "/my-device",
        component: Devices,
        meta: { breadcrumb: [{ parent: "Dashboard", label: "Devices" }] },
        internal: false,
    },
    {
        path: "/integrations",
        component: IntegrationStatus,
        meta: { breadcrumb: [{ parent: "Integrations", label: "Integrations" }] },
        internal: true,
    },
    {
        path: "/labelling/*",
        component: Labelling,
        meta: { breadcrumb: [{ parent: "Dashboard", label: "Labelling" }] },
        internal: true,
    },
    {
        path: "/notifications",
        component: Notifications,
        meta: { breadcrumb: [{ parent: "Dashboard", label: "Notifications" }] },
        internal: false,
    },
    {
        path: "/work-items",
        component: WorkItems,
        meta: { breadcrumb: [{ parent: "Dashboard", label: "Repairs" }] },
        internal: false,
    },
    {
        path: "/work-items/:publicId",
        component: WorkItemDetail,
        // The regex makes the top-bar breadcrumb read "Assignments" instead of the
        // raw "WORK-ITEMS/WI-…" path echo (the page's own header carries the title).
        meta: { breadcrumb: [{ parent: "Repairs", label: "Repairs", regex: "/work-items/(.*?)" }] },
        internal: false,
    },
    {
        path: "/inbox",
        component: InboxRedirect,
        meta: { breadcrumb: [{ parent: "Dashboard", label: "Notifications" }] },
        internal: false,
    },
    {
        // Durable image links from bookmarked-images CSV exports (ART-3122):
        // resolves a fresh presign of the original frame and redirects to it.
        path: "/bookmark-image/:customerId/:eventId",
        component: BookmarkImage,
        meta: { breadcrumb: [{ parent: "Dashboard", label: "Bookmarked Image" }] },
        internal: false,
    },
    {
        // Durable photo links from the detections / work-package CSV exports
        // (image_link column): same mechanism, keyed by detection id.
        path: "/detection-image/:customerId/:objectId",
        component: DetectionImage,
        meta: { breadcrumb: [{ parent: "Dashboard", label: "Detection Photo" }] },
        internal: false,
    },
    ...universalRoutes,
]
